import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Backdrop, Fade, InputAdornment, Modal, TextField, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import { addAcceptedCreator, exportCampaignApplication, fetchCampaignApplication } from '../live-campaign-repository';
import { WindowWidthContext } from "../../context/user-details-context";
import { useLocation, useNavigate } from "react-router-dom";
import AcceptedApplicantsList from "./accepted-applicants-list";
import '../../../css/dashboard.css';
import '../../../css/table-list.css';

export default function AcceptedCampaignApplicationDashboard() {
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = window.location.pathname;
    const { campaignId, campaignName, noOfCreators, campaignClosed, page } = location.state;
    const windowWidth = useContext(WindowWidthContext);
    const [creators, setCreators] = useState([]);
    const [apiFailed, setApiFailed] = useState(false);
    const [totalResults, setTotalResults] = useState();
    const [loading, setLoading] = useState(false);
    const [currentApplicationsPage, setCurrentApplicationsPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedApplicants, setSelectedApplicants] = useState([]);
    const [followersFilterValue, setFollowersFilterValue] = useState({ min: '', max: '' });
    const [rateAdded, setRateAdded] = useState();
    const [open, setOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [creatorAdded, setCreatorAdded] = useState();
    const [error, setError] = useState('');

    useEffect(() => {
        fetchCampaignApplication(setLoading, campaignId, currentApplicationsPage, rowsPerPage,
            setTotalResults, setApiFailed, setTotalPages, setCreators, followersFilterValue);
    }, [currentApplicationsPage, campaignId, rowsPerPage, followersFilterValue, rateAdded, creatorAdded]);

    const handleArrowBack = () => {
        const pathSegments = pathname.split('/');
        const startPath = '/' + pathSegments[1];
        navigate(`${startPath}/${page === 'details' ? 'details' : 'live'}`, { state: { campaignid: campaignId } });
    }

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const devLoginModalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column'
    };

    const handleAddCreator = () => {
        if (!phoneNumber || phoneNumber.length !== 10) {
            setError('Please enter a valid 10-digit phone number');
            return;
        }

        const phoneNumberWithPrefix = `91${phoneNumber}`;
        
        addAcceptedCreator(campaignId, phoneNumberWithPrefix, setCreatorAdded)
            .then((response) => {
                if (response?.error) {
                    setError(response.error);
                } else {
                    setPhoneNumber('');
                    setError('');
                    handleClose();
                }
            })
            .catch((err) => {
                setError('Failed to add creator. Please try again.');
            });
    };

    return (
        <>
            <div className="campaign-application-head">
                <ArrowBackIcon className="arrow-back" sx={{ color: "#9c27b0" }} onClick={() => handleArrowBack()}></ArrowBackIcon>
                <h1 className="campaign-appllication-header-name">Applicants</h1>
            </div>
            <div className={windowWidth <= 799 ? "campaign-application campaign-application-mobile" : "campaign-application"}>
                <div className={windowWidth <= 799 ? "campaign-name-div-mobile" : "campaign-name-div"}>
                    <h1 className="campaign-name-text">Campaign Name: <Tooltip title={campaignName}><span style={{ color: '#f48fb1' }}>{campaignName}</span></Tooltip></h1>
                </div>
                <div className={windowWidth <= 799 ? "campaign-application-head-actions-mobile" : "campaign-application-head-actions"}>
                    <div className="export-application-mobile">
                        <Button variant="contained" disabled={loading} sx={{
                            backgroundColor: '#e563a4', '&:hover': {
                                backgroundColor: '#e326a4'
                            }
                        }} onClick={handleClickOpen} startIcon={<AddIcon />}>Add Creator</Button>
                    </div>
                    <div className="export-application-mobile">
                        <Button variant="contained" disabled={apiFailed || loading} sx={{
                            backgroundColor: '#e563a4', '&:hover': {
                                backgroundColor: '#e326a4'
                            }
                        }} onClick={() => { exportCampaignApplication(campaignId, false, false, false) }} startIcon={<FileDownloadIcon />}>Export</Button>
                    </div>
                </div>
            </div>
            <AcceptedApplicantsList
                selectedApplicants={selectedApplicants} setSelectedApplicants={setSelectedApplicants}
                rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} fromPage={page} totalPages={totalPages} loading={loading} totalResults={totalResults} apiFailed={apiFailed}
                creators={creators} currentPage={currentApplicationsPage} setCurrentPage={setCurrentApplicationsPage} noOfCreators={noOfCreators}
                setFollowersFilterValue={setFollowersFilterValue} campaignId={campaignId} setRateAdded={setRateAdded} campaignClosed={campaignClosed} setCreatorAdded={setCreatorAdded} />
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={devLoginModalStyle}>
                        <Typography sx={{ textAlign: 'center', fontWeight: '600' }} id="transition-modal-title" variant="h5" component="h2">
                            Add Creator
                        </Typography>
                        <TextField 
                            sx={{ marginTop: '15px' }} 
                            id="outlined-basic" 
                            label="Enter Phone" 
                            variant="outlined" 
                            value={phoneNumber} 
                            onChange={(e) => {
                                setPhoneNumber(e.target.value.replace(/\D/g, ''));
                                setError('');
                            }}
                            error={!!error}
                            helperText={error}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        91
                                    </InputAdornment>
                                ),
                            }} 
                            inputProps={{ maxLength: 10 }} 
                        />
                        <Button 
                            sx={{
                                marginTop: '15px', 
                                backgroundColor: '#e563a4', 
                                borderRadius: '21px', 
                                padding: '9px 38px', 
                                textTransform: 'capitalize', 
                                '&:hover': {
                                    backgroundColor: '#e326a4',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#e563a4',
                                    opacity: 0.5
                                }
                            }} 
                            variant="contained" 
                            disabled={!phoneNumber || phoneNumber.length !== 10}
                            onClick={handleAddCreator}
                        >
                            Add Creator
                        </Button>
                    </Box>
                </Fade>
            </Modal>
        </>
    )
}
